(() => {
    const STORAGE_KEY = "market_choice";
    const COOKIE = "market_choice";
    const STORAGE_DAYS = 365;

    const FALLBACK_REGION_CODE = "GB";

    const IP_API = "https://free.freeipapi.com/api/json";
    const IP_CACHE_KEY = "geo_ip_data_v1";
    const IP_CACHE_DAYS = 7;

    const cookieManager = {
        set(name, value, days) {
            const expiresAt = new Date(Date.now() + days * 864e5);
            document.cookie = `${name}=${encodeURIComponent(value)};expires=${expiresAt.toUTCString()};path=/;SameSite=Lax`;
        },

        get(name) {
            const allCookies = document.cookie.split("; ");
            for (const cookie of allCookies) {
                const [key, ...value] = cookie.split("=");
                if (key === name) return decodeURIComponent(value.join("="));
            }
            return null;
        },

        saveChoice(market) {
            try {
                localStorage.setItem(
                    STORAGE_KEY,
                    JSON.stringify({ value: market, timestamp: Date.now() })
                );
            } catch { }
            cookieManager.set(COOKIE, market, STORAGE_DAYS);
        },

        getChoice() {
            try {
                const item = localStorage.getItem(STORAGE_KEY);
                if (item) {
                    const parsed = JSON.parse(item);
                    return parsed?.value || null;
                }
            } catch { }
            return cookieManager.get(COOKIE);
        },

        parseConfig() {
            try {
                const el = document.getElementById("market-prompts");
                if (!el) return null;
                return JSON.parse(el.textContent || "null");
            } catch {
                return null;
            }
        },
    };

    const fetchIPData = async () => {
        try {
            const cachedRaw = sessionStorage.getItem(IP_CACHE_KEY);
            if (cachedRaw) {
                const cached = JSON.parse(cachedRaw);
                const ts = Number(cached?.timestamp || 0);
                const ageMs = Date.now() - ts;
                if (ts && ageMs >= 0 && ageMs < IP_CACHE_DAYS * 864e5) return cached.data || null;
            }
        } catch { }

        try {
            const response = await fetch(IP_API, { method: "GET" });
            if (!response.ok) return null;

            const data = await response.json();
            try {
                sessionStorage.setItem(
                    IP_CACHE_KEY,
                    JSON.stringify({ timestamp: Date.now(), data })
                );
            } catch { }
            return data;
        } catch {
            return null;
        }
    };

    const getHostname = (raw) => {
        if (!raw) return "";
        let url = String(raw).trim();

        if (url.includes("{{path}}")) url = url.replace("{{path}}", "");
        if (!/^https?:\/\//i.test(url)) url = "https://" + url;

        try {
            return new URL(url).hostname.toLowerCase();
        } catch {
            return "";
        }
    };

    const currentHost = window.location.hostname.toLowerCase();

    const isCurrentSite = (entry) => {
        const target = getHostname(entry?.domain);
        if (!target) return false;
        if (currentHost === target) return true;
        if (currentHost.endsWith("." + target)) return true;
        return false;
    };

    const modal = document.getElementById("market-redirect-modal");
    const elements = {
        title: modal?.querySelector("[data-market-title]"),
        body: modal?.querySelector("[data-market-body]"),
        btnSwitch: modal?.querySelector("[data-market-switch]"),
        btnStay: modal?.querySelector("[data-market-stay]"),
        btnClose: modal?.querySelector("[data-market-close]"),
        countdown: modal?.querySelector("[data-market-countdown]"),
    };

    const open = () => {
        if (!modal) return;
        modal.hidden = false;
        modal.setAttribute("aria-hidden", "false");
        document.body.classList.add("no-scroll");
    };

    const close = () => {
        if (!modal) return;
        stopCountdown();
        modal.hidden = true;
        modal.setAttribute("aria-hidden", "true");
        document.body.classList.remove("no-scroll");
    };

    let countdownTimer = null;
    let countdownDeadline = 0;
    const countdownSecs = 10;

    const updateCountdownText = (s) => {
        if (!elements.countdown) return;
        elements.countdown.textContent =
            s > 0
                ? `Auto-redirecting you in ${s} second${s === 1 ? "" : "s"}…`
                : "Redirecting…";
    };

    const stopCountdown = () => {
        if (countdownTimer) {
            clearInterval(countdownTimer);
            countdownTimer = null;
        }
        if (elements.countdown) elements.countdown.style.display = "";
    };

    const doRedirect = (entry) => {
        if (!entry?.domain) return;

        cookieManager.saveChoice("SWITCHED_" + (entry.country_code || ""));
        let url = entry.domain;

        if (url.includes("{{path}}")) {
            url = url.replace("{{path}}", window.location.pathname + window.location.search);
        }

        window.location.href = url;
    };

    const startCountdown = (entry) => {
        stopCountdown();
        if (!elements.countdown || !entry?.domain) return;

        countdownDeadline = Date.now() + countdownSecs * 1000;
        updateCountdownText(countdownSecs);

        countdownTimer = setInterval(() => {
            const remaining = Math.max(
                0,
                Math.ceil((countdownDeadline - Date.now()) / 1000)
            );
            updateCountdownText(remaining);
            if (remaining <= 0) {
                stopCountdown();
                doRedirect(entry);
            }
        }, 1000);

        elements.countdown.style.display = "";
    };

    const hydrate = (entry) => {
        if (!modal || !entry) return false;

        if (elements.title) elements.title.textContent = entry.title || "";
        if (elements.body) {
            elements.body.innerHTML = typeof entry.body_html === "string" ? entry.body_html : "";
        }

        if (elements.btnSwitch) {
            elements.btnSwitch.textContent = entry.cta_switch || "Yes, please";
            elements.btnSwitch.onclick = (e) => {
                e.preventDefault();
                stopCountdown();
                doRedirect(entry);
            };
        }

        if (elements.btnStay) {
            elements.btnStay.textContent = entry.cta_stay || "No, thanks";
            elements.btnStay.onclick = () => {
                cookieManager.saveChoice("STAY_" + (FALLBACK_REGION_CODE || "GB"));
                close();
            };
        }

        if (elements.btnClose) {
            elements.btnClose.onclick = () => {
                cookieManager.saveChoice("CLOSE");
                close();
            };
        }

        if (elements.countdown) {
            if (entry.domain) startCountdown(entry);
            else {
                elements.countdown.textContent = "";
                elements.countdown.style.display = "none";
            }
        }

        window.addEventListener(
            "keyup",
            (event) => event.code === "Escape" && close(),
            { once: true }
        );

        return true;
    };

    const config = cookieManager.parseConfig() || { ukCountryCode: "GB", markets: [] };

    const byCountry = {};
    const byLangPrefix = [];

    (config.markets || []).forEach((m) => {
        if (!m) return;

        const cc = String(m.country_code || "").toUpperCase();
        if (cc) byCountry[cc] = m;

        const lk = String(m.language_key || "").toLowerCase();
        if (lk) {
            const prefix = lk.split("-")[0];
            byLangPrefix.push({ prefix, entry: m });
        }
    });

    const langFallback = () => {
        const langs = (navigator.languages || [navigator.language])
            .filter(Boolean)
            .map((l) => String(l).toLowerCase());

        for (const l of langs) {
            const prefix = l.split("-")[0];
            const hit = byLangPrefix.find((r) => r.prefix === prefix);
            if (hit) return hit.entry;
        }
        return null;
    };

    const normalizeLower = (v) => String(v || "").trim().toLowerCase();
    const normalizeUpper = (v) => String(v || "").trim().toUpperCase();

    const resolveGbSubRegionCode = async () => {
        const geo = await fetchIPData();
        if (!geo) return null;

        const countryName = normalizeLower(geo.countryName);
        const countryCode = normalizeUpper(geo.countryCode);

        if (countryCode === "IE" || countryName === "ireland") return "IE";

        const regionName = normalizeLower(geo.regionName);
        const regionCode = normalizeUpper(geo.regionCode);

        if (regionCode === "NIR" || regionName === "northern ireland") return "NI";

        return null;
    };

    const MarketSwitcher = (window.MarketSwitcher = window.MarketSwitcher || {});

    MarketSwitcher.getChoice = cookieManager.getChoice;
    MarketSwitcher.saveChoice = cookieManager.saveChoice;

    MarketSwitcher.promptFor = (cc) => {
        const code = normalizeUpper(cc);
        const entry = byCountry[code];
        if (!entry || isCurrentSite(entry)) return false;
        hydrate(entry);
        open();
        return true;
    };

    MarketSwitcher.switchTo = (cc) => {
        const entry = byCountry[normalizeUpper(cc)];
        if (entry?.domain && !isCurrentSite(entry)) {
            stopCountdown();
            doRedirect(entry);
        }
    };

    MarketSwitcher.close = close;
    MarketSwitcher.open = open;
    MarketSwitcher.isCurrentSite = isCurrentSite;
    MarketSwitcher.getEntry = (cc) => byCountry[normalizeUpper(cc)] || null;
    MarketSwitcher.getDetected = async () => {

        const ukCode = normalizeUpper(config.ukCountryCode || "GB");

        try {
            const res = await fetch("/browsing_context_suggestions.json", { credentials: "same-origin" });
            const data = await res.json();
            const country = data?.detected_values?.country || null;
            let code = normalizeUpper(country?.handle || country?.code || "") || null;

            if (!code) code = FALLBACK_REGION_CODE;

            if (code === ukCode) {
                const sub = await resolveGbSubRegionCode();
                if (sub) code = sub;
            }

            if (code === "NI" && !byCountry.NI && byCountry.IE) code = "IE";
            if (!code) code = FALLBACK_REGION_CODE;

            return { code, entry: byCountry[code] || null };
        } catch {
            return { code: null, entry: null };
        }
    };

    if (cookieManager.getChoice()) return;

    fetch("/browsing_context_suggestions.json", { credentials: "same-origin" })
        .then((r) => r.json())
        .then(async (data) => {
            const country = data?.detected_values?.country || null;
            let code = normalizeUpper(country?.handle || country?.code || "") || null;

            if (!code) code = FALLBACK_REGION_CODE;

            const ukCode = normalizeUpper(config.ukCountryCode || "GB");
            if (code === ukCode) {
                const sub = await resolveGbSubRegionCode();
                if (sub) code = sub;
            }

            if (code === "NI" && !byCountry.NI && byCountry.IE) {
                code = "IE";
            }

            if (!code) code = FALLBACK_REGION_CODE;

            if (code === ukCode) {
                const lf = langFallback();
                if (lf && !isCurrentSite(lf)) {
                    if (hydrate(lf)) open();
                }
                return;
            }

            const entry = byCountry[code];
            if (!entry || isCurrentSite(entry)) return;
            if (hydrate(entry)) open();
        })
        .catch(() => {
            const fb = langFallback();
            if (fb && !isCurrentSite(fb)) {
                if (hydrate(fb)) open();
            }
        });
})();

